import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { GoalsRepository } from '../domain/goals.repository';
import {
  GoalAggregation,
  GoalCalculationStatus,
  GoalCollectionMode,
  GoalConfigurationData,
  GoalCorrectionType,
  GoalDailySummary,
  GoalDayItem,
  GoalDefinition,
  GoalDefinitionInput,
  GoalDefinitionOption,
  GoalPeriodConfiguration,
  GoalPeriodItem,
  GoalPeriodTemplateItem,
  GoalPeriodTemplateType,
  GoalSavedTemplate,
  GoalSavedTemplateDetails,
  GoalSavedTemplateItemInput,
  GoalTemplateConflictStrategy,
  GoalTemplateExchangeRow,
  GoalTemplateImportResult,
  GoalPeriodType,
  GoalSourceConfiguration,
  GoalSourceOption,
  GoalSourceOptionsData,
  GoalSourceType,
  GoalTaskKind,
  GoalTemplateData,
  GoalTemplateItem,
  GoalUnit,
  GoalWeeklyModelItemInput
} from '../models/goal.model';

interface GoalDayRow {
  target_id: number;
  measurement_id: number;
  goal_definition_id: number;
  target_date: string;
  name: string;
  description: string | null;
  unit: GoalUnit;
  unit_label: string | null;
  collection_mode: GoalCollectionMode;
  source_type: GoalSourceType;
  target_value: number | string;
  position: number;
  is_exception: boolean;
  manual_value: number | string | null;
  calculated_value: number | string | null;
  correction_type: GoalCorrectionType | null;
  correction_value: number | string | null;
  retained_value: number | string | null;
  calculation_status: GoalCalculationStatus;
  calculation_error: string | null;
  progress_percent: number | string | null;
}

interface GoalDefinitionRow {
  id: number;
  name: string;
  description: string | null;
  unit: GoalUnit;
  unit_label: string | null;
  collection_mode: GoalCollectionMode;
  source_type: GoalSourceType;
  period_type: GoalPeriodType;
  period_configuration: GoalPeriodConfiguration;
  period_target_value: number | string | null;
  aggregation?: GoalAggregation | null;
  source_configuration?: GoalSourceConfiguration;
  position: number;
  is_archived?: boolean;
  archived_at?: string | null;
}

interface GoalTemplateRow {
  id: number;
  goal_definition_id: number;
  weekday: number;
  target_value: number | string;
  position: number;
  is_active: boolean;
}

interface TaskDefinitionRow {
  id: number;
  title: string;
  kind: GoalTaskKind;
  routine_measure_type?: 'QUANTITY' | 'TIME' | null;
  routine_measure_unit?: string | null;
  routine_default_quantity?: number | string | null;
}

interface ListRow {
  id: number;
  name: string;
}

interface ActivityRow {
  id: number;
  name: string;
}

interface GoalPeriodRow {
  target_id: number;
  measurement_id: number;
  goal_definition_id: number;
  period_type: GoalPeriodType;
  period_start: string;
  period_end: string;
  name: string;
  description: string | null;
  unit: GoalUnit;
  unit_label: string | null;
  collection_mode: GoalCollectionMode;
  source_type: GoalSourceType;
  target_value: number | string;
  position: number;
  manual_value: number | string | null;
  calculated_value: number | string | null;
  correction_type: GoalCorrectionType | null;
  correction_value: number | string | null;
  retained_value: number | string | null;
  calculation_status: GoalCalculationStatus;
  calculation_error: string | null;
  progress_percent: number | string | null;
  days_remaining: number | string;
}

interface GoalPeriodTemplateRow {
  id: number;
  goal_definition_id: number;
  name: string;
  target_value: number | string;
  position: number;
}

interface GoalSavedTemplateRow {
  id: number;
  name: string;
  template_kind: 'DAY' | 'WEEK' | 'PERIOD';
  period_type: GoalPeriodTemplateType | null;
  week_mode: 'DIRECT' | 'DAY_TYPES' | null;
}

interface GoalSummaryRow {
  target_count: number | string;
  measured_count: number | string;
  achieved_count: number | string;
  source_issue_count: number | string;
  completeness_percent: number | string;
  score_percent: number | string | null;
  is_provisional: boolean;
}

@Injectable()
export class SupabaseGoalsRepository extends GoalsRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async loadDay(targetDate: string, refresh = true): Promise<GoalDayItem[]> {
    const { data, error } = await this.client.rpc('get_goal_day', {
      p_target_date: targetDate,
      p_refresh: refresh
    });
    if (error) throw error;
    return ((data ?? []) as GoalDayRow[]).map(row => this.mapDayRow(row));
  }

  async loadWeek(weekStart: string, refresh = true): Promise<GoalDayItem[]> {
    const { data, error } = await this.client.rpc('get_goal_week', {
      p_week_start: weekStart,
      p_refresh: refresh
    });
    if (error) throw error;
    return ((data ?? []) as GoalDayRow[]).map(row => this.mapDayRow(row));
  }

  async loadExistingWeek(weekStart: string): Promise<GoalDayItem[]> {
    const { data, error } = await this.client.rpc('get_existing_goal_week', {
      p_week_start: weekStart
    });
    if (error) throw error;
    return ((data ?? []) as GoalDayRow[]).map(row => this.mapDayRow(row));
  }

  async loadDailySummary(targetDate: string, refresh = true): Promise<GoalDailySummary> {
    const { data, error } = await this.client.rpc('get_goal_daily_summary', {
      p_target_date: targetDate,
      p_refresh: refresh
    });
    if (error) throw error;

    const row = ((data ?? []) as GoalSummaryRow[])[0];
    if (!row) {
      return {
        targetCount: 0,
        measuredCount: 0,
        achievedCount: 0,
        sourceIssueCount: 0,
        completenessPercent: 100,
        scorePercent: null,
        isProvisional: false
      };
    }

    return {
      targetCount: this.toNumber(row.target_count),
      measuredCount: this.toNumber(row.measured_count),
      achievedCount: this.toNumber(row.achieved_count),
      sourceIssueCount: this.toNumber(row.source_issue_count),
      completenessPercent: this.toNumber(row.completeness_percent),
      scorePercent: this.toNullableNumber(row.score_percent),
      isProvisional: row.is_provisional
    };
  }

  async loadPeriods(referenceDate: string, refresh = true): Promise<GoalPeriodItem[]> {
    const { data, error } = await this.client.rpc('get_goal_periods', {
      p_reference_date: referenceDate,
      p_refresh: refresh
    });
    if (error) throw error;
    return ((data ?? []) as GoalPeriodRow[]).map(row => ({
      targetId: row.target_id,
      measurementId: row.measurement_id,
      goalDefinitionId: row.goal_definition_id,
      periodType: row.period_type,
      periodStart: row.period_start,
      periodEnd: row.period_end,
      name: row.name,
      description: row.description,
      unit: row.unit,
      unitLabel: row.unit_label,
      collectionMode: row.collection_mode,
      sourceType: row.source_type,
      targetValue: this.toNumber(row.target_value),
      position: row.position,
      manualValue: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.manual_value) ?? 0) : this.toNullableNumber(row.manual_value),
      calculatedValue: this.toNullableNumber(row.calculated_value),
      correctionType: row.correction_type,
      correctionValue: this.toNullableNumber(row.correction_value),
      retainedValue: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.retained_value) ?? 0) : this.toNullableNumber(row.retained_value),
      calculationStatus: row.calculation_status,
      calculationError: row.calculation_error,
      progressPercent: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.progress_percent) ?? 0) : this.toNullableNumber(row.progress_percent),
      daysRemaining: this.toNumber(row.days_remaining)
    }));
  }

  async loadPeriodTemplate(periodType: GoalPeriodTemplateType): Promise<GoalPeriodTemplateItem[]> {
    const { data, error } = await this.client.rpc('get_goal_period_template', {
      p_period_type: periodType
    });
    if (error) throw error;
    return ((data ?? []) as GoalPeriodTemplateRow[]).map(row => ({
      id: row.id,
      goalDefinitionId: row.goal_definition_id,
      name: row.name,
      targetValue: this.toNumber(row.target_value),
      position: row.position
    }));
  }

  async saveDayTemplateFromDate(targetDate: string): Promise<number> {
    const { data, error } = await this.client.rpc('save_goal_day_template_from_date', {
      p_target_date: targetDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async applyDayTemplate(targetDate: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_goal_day_template', {
      p_target_date: targetDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async loadSavedTemplates(): Promise<GoalSavedTemplate[]> {
    const { data, error } = await this.client.rpc('get_goal_saved_templates');
    if (error) throw error;
    return ((data ?? []) as GoalSavedTemplateRow[]).map(row => ({
      id: row.id,
      name: row.name,
      templateKind: row.template_kind,
      periodType: row.period_type,
      weekMode: row.week_mode
    }));
  }

  async loadSavedTemplateDetails(templateId: number): Promise<GoalSavedTemplateDetails> {
    const { data, error } = await this.client.rpc('get_goal_saved_template_details', {
      p_template_id: templateId
    });
    if (error) throw error;
    return data as GoalSavedTemplateDetails;
  }

  async updateSavedTemplate(
    templateId: number,
    name: string,
    items: GoalSavedTemplateItemInput[],
    dayTemplateIds: Array<number | null>
  ): Promise<void> {
    const { error } = await this.client.rpc('update_goal_saved_template', {
      p_template_id: templateId,
      p_name: name,
      p_items: items.map(item => ({
        goal_definition_id: item.goalDefinitionId,
        target_value: item.targetValue,
        position: item.position,
        weekday: item.weekday
      })),
      p_day_template_ids: dayTemplateIds
    });
    if (error) throw error;
  }

  async exportSavedTemplates(templateIds: number[] | null): Promise<GoalTemplateExchangeRow[]> {
    const { data, error } = await this.client.rpc('export_goal_saved_templates', {
      p_template_ids: templateIds
    });
    if (error) throw error;
    return (data ?? []) as GoalTemplateExchangeRow[];
  }

  async importSavedTemplates(
    rows: GoalTemplateExchangeRow[],
    conflictStrategy: GoalTemplateConflictStrategy
  ): Promise<GoalTemplateImportResult> {
    const { data, error } = await this.client.rpc('import_goal_saved_templates', {
      p_rows: rows,
      p_conflict_strategy: conflictStrategy
    });
    if (error) throw error;
    const result = (data ?? {}) as Partial<GoalTemplateImportResult>;
    return {
      importedTemplates: Number(result.importedTemplates ?? 0),
      skippedTemplates: Number(result.skippedTemplates ?? 0),
      manualizedDefinitions: Number(result.manualizedDefinitions ?? 0)
    };
  }

  async saveNamedDayTemplate(name: string, targetDate: string): Promise<number> {
    const { data, error } = await this.client.rpc('save_named_goal_day_template', {
      p_name: name,
      p_target_date: targetDate
    });
    if (error) throw error;
    return Number(data);
  }

  async applyNamedDayTemplate(templateId: number, targetDate: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_named_goal_day_template', {
      p_template_id: templateId,
      p_target_date: targetDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async saveNamedWeekTemplateDirect(name: string, weekStart: string): Promise<number> {
    const { data, error } = await this.client.rpc('save_named_goal_week_template_direct', {
      p_name: name,
      p_week_start: weekStart
    });
    if (error) throw error;
    return Number(data);
  }

  async saveNamedWeekTemplateFromDays(name: string, dayTemplateIds: Array<number | null>): Promise<number> {
    const { data, error } = await this.client.rpc('save_named_goal_week_template_from_days', {
      p_name: name,
      p_day_template_ids: dayTemplateIds
    });
    if (error) throw error;
    return Number(data);
  }

  async applyNamedWeekTemplate(templateId: number, weekStart: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_named_goal_week_template', {
      p_template_id: templateId,
      p_week_start: weekStart
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async saveNamedPeriodTemplate(
    name: string,
    periodType: GoalPeriodTemplateType,
    referenceDate: string
  ): Promise<number> {
    const { data, error } = await this.client.rpc('save_named_goal_period_template', {
      p_name: name,
      p_period_type: periodType,
      p_reference_date: referenceDate
    });
    if (error) throw error;
    return Number(data);
  }

  async applyNamedPeriodTemplate(templateId: number, referenceDate: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_named_goal_period_template', {
      p_template_id: templateId,
      p_reference_date: referenceDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async loadDefinitions(): Promise<GoalDefinitionOption[]> {
    const { data, error } = await this.client
      .from('goal_definitions')
      .select('id,name,description,unit,unit_label,collection_mode,source_type,period_type,period_configuration,period_target_value,position')
      .eq('is_archived', false)
      .order('position')
      .order('id');
    if (error) throw error;

    return ((data ?? []) as GoalDefinitionRow[]).map(row => this.mapDefinitionOption(row));
  }

  async loadSourceOptions(): Promise<GoalSourceOptionsData> {
    const [tasksResult, listsResult, activitiesResult, measurementsResult, sportsResult, cultureResult] = await Promise.all([
      this.client
        .from('task_definitions')
        .select('id,title,kind,routine_measure_type,routine_measure_unit,routine_default_quantity')
        .is('archived_at', null)
        .order('title'),
      this.client
        .from('lists')
        .select('id,name')
        .eq('is_archived', false)
        .order('name'),
      this.client
        .from('time_activities')
        .select('id,name')
        .is('archived_at', null)
        .order('name'),
      this.client.from('measurement_types')
        .select('id,name,kind,unit')
        .is('archived_at', null)
        .order('name'),
      this.client.from('sport_definitions').select('id,name')
        .is('archived_at',null).order('name'),
      this.client.from('culture_items').select('type').order('type')
    ]);

    if (tasksResult.error) throw tasksResult.error;
    if (listsResult.error) throw listsResult.error;
    if (activitiesResult.error) throw activitiesResult.error;
    if (measurementsResult.error) throw measurementsResult.error;
    if (sportsResult.error) throw sportsResult.error;
    if (cultureResult.error) throw cultureResult.error;

    const taskDefinitions: GoalSourceOption[] = ((tasksResult.data ?? []) as TaskDefinitionRow[])
      .map(row => ({
        id: row.id,
        label: row.title,
        kind: row.kind,
        measureType: row.routine_measure_type ?? null,
        measureUnit: row.routine_measure_unit ?? null,
        defaultQuantity: row.routine_default_quantity === null || row.routine_default_quantity === undefined
          ? null
          : Number(row.routine_default_quantity)
      }));

    return {
      sportDefinitions: (sportsResult.data ?? []).map(row => ({id:Number(row.id),label:String(row.name)})),
      cultureTypes: [...new Set((cultureResult.data ?? []).map(row => String(row.type)))],
      measurementTypes: (measurementsResult.data ?? []).map(row => ({
        id: Number(row.id), label: String(row.name),
        kind: row.kind as 'DURATION' | 'QUANTITY' | 'PERCENTAGE',
        unitLabel: row.unit ?? null
      })),
      taskDefinitions,
      routineDefinitions: taskDefinitions.filter(option => option.kind === 'ROUTINE'),
      lists: ((listsResult.data ?? []) as ListRow[]).map(row => ({ id: row.id, label: row.name })),
      activities: ((activitiesResult.data ?? []) as ActivityRow[]).map(row => ({ id: row.id, label: row.name }))
    };
  }

  async loadTemplateData(): Promise<GoalTemplateData> {
    const [definitionsResult, templateResult] = await Promise.all([
      this.client
        .from('goal_definitions')
        .select('id,name,description,unit,unit_label,collection_mode,source_type,period_type,period_configuration,period_target_value,position')
        .eq('is_archived', false)
        .eq('period_type', 'DAILY')
        .order('position')
        .order('id'),
      this.client
        .from('goal_template_items')
        .select('id,goal_definition_id,weekday,target_value,position,is_active')
        .order('goal_definition_id')
        .order('weekday')
    ]);

    if (definitionsResult.error) throw definitionsResult.error;
    if (templateResult.error) throw templateResult.error;

    return {
      definitions: ((definitionsResult.data ?? []) as GoalDefinitionRow[])
        .map(row => this.mapDefinitionOption(row)),
      templateItems: ((templateResult.data ?? []) as GoalTemplateRow[]).map(row => ({
        id: row.id,
        goalDefinitionId: row.goal_definition_id,
        weekday: row.weekday,
        targetValue: this.toNumber(row.target_value),
        position: row.position,
        isActive: row.is_active
      }))
    };
  }

  async loadConfiguration(): Promise<GoalConfigurationData> {
    const [definitionsResult, templateResult, tasksResult, listsResult, activitiesResult, historyResult, periodHistoryResult] = await Promise.all([
      this.client
        .from('goal_definitions')
        .select('id,name,description,unit,unit_label,collection_mode,source_type,aggregation,source_configuration,period_type,period_configuration,period_target_value,position,is_archived,archived_at')
        .order('is_archived')
        .order('position')
        .order('id'),
      this.client
        .from('goal_template_items')
        .select('id,goal_definition_id,weekday,target_value,position,is_active')
        .order('goal_definition_id')
        .order('weekday'),
      this.client
        .from('task_definitions')
        .select('id,title,kind,routine_measure_type,routine_measure_unit,routine_default_quantity')
        .is('archived_at', null)
        .order('title'),
      this.client
        .from('lists')
        .select('id,name')
        .eq('is_archived', false)
        .order('name'),
      this.client
        .from('time_activities')
        .select('id,name')
        .is('archived_at', null)
        .order('name'),
      this.client
        .from('daily_goal_targets')
        .select('goal_definition_id')
        .limit(10000),
      this.client
        .from('goal_period_targets')
        .select('goal_definition_id')
        .limit(10000)
    ]);

    if (definitionsResult.error) throw definitionsResult.error;
    if (templateResult.error) throw templateResult.error;
    if (tasksResult.error) throw tasksResult.error;
    if (listsResult.error) throw listsResult.error;
    if (activitiesResult.error) throw activitiesResult.error;
    if (historyResult.error) throw historyResult.error;
    if (periodHistoryResult.error) throw periodHistoryResult.error;

    const historyIds = new Set<number>([
      ...(historyResult.data ?? []).map(row => Number(row.goal_definition_id)),
      ...(periodHistoryResult.data ?? []).map(row => Number(row.goal_definition_id))
    ]);
    const taskRows = (tasksResult.data ?? []) as TaskDefinitionRow[];

    const taskDefinitions: GoalSourceOption[] = taskRows.map(row => ({
      id: row.id,
      label: row.title,
      kind: row.kind,
      measureType: row.routine_measure_type ?? null,
      measureUnit: row.routine_measure_unit ?? null,
      defaultQuantity: row.routine_default_quantity === null || row.routine_default_quantity === undefined
        ? null
        : Number(row.routine_default_quantity)
    }));

    return {
      definitions: ((definitionsResult.data ?? []) as GoalDefinitionRow[]).map(row => ({
        ...this.mapDefinitionOption(row),
        aggregation: row.aggregation ?? null,
        sourceConfiguration: row.source_configuration ?? {},
        isArchived: row.is_archived ?? false,
        archivedAt: row.archived_at ?? null,
        hasHistory: historyIds.has(row.id)
      } satisfies GoalDefinition)),
      templateItems: ((templateResult.data ?? []) as GoalTemplateRow[]).map(row => ({
        id: row.id,
        goalDefinitionId: row.goal_definition_id,
        weekday: row.weekday,
        targetValue: this.toNumber(row.target_value),
        position: row.position,
        isActive: row.is_active
      } satisfies GoalTemplateItem)),
      taskDefinitions,
      routineDefinitions: taskDefinitions.filter(option => option.kind === 'ROUTINE'),
      lists: ((listsResult.data ?? []) as ListRow[]).map(row => ({ id: row.id, label: row.name })),
      activities: ((activitiesResult.data ?? []) as ActivityRow[]).map(row => ({ id: row.id, label: row.name }))
    };
  }

  async createDefinition(input: GoalDefinitionInput): Promise<number> {
    const { data, error } = await this.client.rpc('save_goal_definition_configuration_v2', {
      p_goal_definition_id: null,
      ...this.toDefinitionRpcInput(input)
    });
    if (error) throw error;
    return Number(data);
  }

  async updateDefinition(definitionId: number, input: GoalDefinitionInput): Promise<void> {
    const { error } = await this.client.rpc('save_goal_definition_configuration_v2', {
      p_goal_definition_id: definitionId,
      ...this.toDefinitionRpcInput(input)
    });
    if (error) throw error;
  }

  async archiveDefinition(definitionId: number): Promise<void> {
    const { error } = await this.client.rpc('set_goal_definition_archived', {
      p_goal_definition_id: definitionId,
      p_archived: true
    });
    if (error) throw error;
  }

  async restoreDefinition(definitionId: number): Promise<void> {
    const { error } = await this.client.rpc('set_goal_definition_archived', {
      p_goal_definition_id: definitionId,
      p_archived: false
    });
    if (error) throw error;
  }

  async replaceWeeklyModel(items: GoalWeeklyModelItemInput[]): Promise<void> {
    const { error } = await this.client.rpc('replace_goal_weekly_model', {
      p_items: items.map(item => ({
        goal_definition_id: item.goalDefinitionId,
        weekday: item.weekday,
        target_value: item.targetValue,
        position: item.position
      }))
    });
    if (error) throw error;
  }

  async applyWeeklyModel(weekStart: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_goal_weekly_model', {
      p_week_start: weekStart
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async replacePeriodTemplateFromPeriod(
    periodType: GoalPeriodTemplateType,
    referenceDate: string
  ): Promise<number> {
    const { data, error } = await this.client.rpc('replace_goal_period_template_from_period', {
      p_period_type: periodType,
      p_reference_date: referenceDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async applyPeriodTemplate(
    periodType: GoalPeriodTemplateType,
    referenceDate: string
  ): Promise<number> {
    const { data, error } = await this.client.rpc('apply_goal_period_template', {
      p_period_type: periodType,
      p_reference_date: referenceDate
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async recalculateDate(targetDate: string): Promise<void> {
    const { error } = await this.client.rpc('recalculate_goal_date', {
      p_target_date: targetDate
    });
    if (error) throw error;
  }

  async setManualValue(measurementId: number, value: number | null): Promise<void> {
    const { data, error } = await this.client
      .from('goal_measurements')
      .update({ manual_value: value })
      .eq('id', measurementId)
      .select('id')
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Le résultat manuel n’a pas pu être enregistré.');
  }

  async setCorrection(
    measurementId: number,
    type: GoalCorrectionType,
    value: number
  ): Promise<void> {
    const { error } = await this.client
      .from('goal_measurements')
      .update({ correction_type: type, correction_value: value })
      .eq('id', measurementId);
    if (error) throw error;
  }

  async clearCorrection(measurementId: number): Promise<void> {
    const { error } = await this.client
      .from('goal_measurements')
      .update({ correction_type: null, correction_value: null })
      .eq('id', measurementId);
    if (error) throw error;
  }

  async setPeriodManualValue(targetId: number, value: number | null): Promise<void> {
    const { error } = await this.client.rpc('set_goal_period_manual_value', {
      p_period_target_id: targetId,
      p_value: value
    });
    if (error) throw error;
  }

  async setPeriodCorrection(targetId: number, type: GoalCorrectionType, value: number): Promise<void> {
    const { error } = await this.client.rpc('set_goal_period_correction', {
      p_period_target_id: targetId,
      p_type: type,
      p_value: value
    });
    if (error) throw error;
  }

  async clearPeriodCorrection(targetId: number): Promise<void> {
    const { error } = await this.client.rpc('clear_goal_period_correction', {
      p_period_target_id: targetId
    });
    if (error) throw error;
  }

  async deactivatePeriodTarget(targetId: number): Promise<void> {
    const { error } = await this.client.rpc('deactivate_goal_period_target', {
      p_period_target_id: targetId
    });
    if (error) throw error;
  }

  async upsertPeriodTarget(
    goalDefinitionId: number,
    referenceDate: string,
    targetValue: number | null
  ): Promise<void> {
    const { error } = await this.client.rpc('upsert_goal_period_target', {
      p_goal_definition_id: goalDefinitionId,
      p_reference_date: referenceDate,
      p_target_value: targetValue
    });
    if (error) throw error;
  }

  async upsertDailyException(
    goalDefinitionId: number,
    targetDate: string,
    targetValue: number,
    position: number | null = null
  ): Promise<void> {
    const { error } = await this.client.rpc('upsert_goal_daily_exception', {
      p_goal_definition_id: goalDefinitionId,
      p_target_date: targetDate,
      p_target_value: targetValue,
      p_position: position
    });
    if (error) throw error;
  }

  async deactivateDailyTarget(targetId: number): Promise<void> {
    const { error } = await this.client.rpc('deactivate_goal_daily_target', {
      p_daily_goal_target_id: targetId
    });
    if (error) throw error;
  }

  private toDefinitionRpcInput(input: GoalDefinitionInput): Record<string, unknown> {
    return {
      p_name: input.name.trim(),
      p_description: input.description?.trim() || null,
      p_unit: input.unit,
      p_unit_label: input.unitLabel?.trim() || null,
      p_collection_mode: input.collectionMode,
      p_source_type: input.sourceType,
      p_aggregation: input.aggregation,
      p_source_configuration: input.sourceConfiguration,
      p_period_type: input.periodType ?? 'DAILY',
      p_period_configuration: input.periodConfiguration ?? {},
      p_period_target_value: input.periodTargetValue ?? null,
      p_position: input.position,
      p_weekly_targets: input.weeklyTargets.map(target => ({
        weekday: target.weekday,
        target_value: target.targetValue,
        position: target.position
      }))
    };
  }

  private mapDefinitionOption(row: GoalDefinitionRow): GoalDefinitionOption {
    return {
      id: row.id,
      name: row.name,
      description: row.description,
      unit: row.unit,
      unitLabel: row.unit_label,
      collectionMode: row.collection_mode,
      sourceType: row.source_type,
      periodType: row.period_type,
      periodConfiguration: row.period_configuration ?? {},
      periodTargetValue: this.toNullableNumber(row.period_target_value),
      position: row.position
    };
  }

  private mapDayRow(row: GoalDayRow): GoalDayItem {
    return {
      targetId: row.target_id,
      measurementId: row.measurement_id,
      goalDefinitionId: row.goal_definition_id,
      targetDate: row.target_date,
      name: row.name,
      description: row.description,
      unit: row.unit,
      unitLabel: row.unit_label,
      collectionMode: row.collection_mode,
      sourceType: row.source_type,
      targetValue: this.toNumber(row.target_value),
      position: row.position,
      isException: row.is_exception,
      manualValue: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.manual_value) ?? 0) : this.toNullableNumber(row.manual_value),
      calculatedValue: this.toNullableNumber(row.calculated_value),
      correctionType: row.correction_type,
      correctionValue: this.toNullableNumber(row.correction_value),
      retainedValue: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.retained_value) ?? 0) : this.toNullableNumber(row.retained_value),
      calculationStatus: row.calculation_status,
      calculationError: row.calculation_error,
      progressPercent: row.collection_mode === 'MANUAL' ? (this.toNullableNumber(row.progress_percent) ?? 0) : this.toNullableNumber(row.progress_percent)
    };
  }

  private toNumber(value: number | string): number {
    const converted = Number(value);
    if (!Number.isFinite(converted)) throw new Error(`Valeur numérique Goals invalide: ${value}`);
    return converted;
  }

  private toNullableNumber(value: number | string | null): number | null {
    return value === null ? null : this.toNumber(value);
  }
}
